import { Flex, Text } from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { ApiKeyRead } from "@nens/rana/api-v1";
import { RanaLoading, RanaModal } from "@nens/ranalib";
import { FormErrors, RanaForm, SubmitFunction } from "@nens/ranalib";

import { cancelButtonText } from "@/translations";

import { getId } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { useEditShareKey } from "../api/edit-share-key";

interface PublicationDetailShareRevokeProps {
  onClose: () => void;
  back: () => void;
  toList: () => void;
  share: ApiKeyRead;
}

const INITIAL_VALUES = {
  confirm: "",
};

type ShareEditFormValues = typeof INITIAL_VALUES;

export function PublicationDetailShareRevoke(props: PublicationDetailShareRevokeProps) {
  const { onClose, share, toList, back } = props;
  const intl = useIntl();
  const publicationId = usePublicationStore(getId);
  const { isMutating, mutate } = useEditShareKey(publicationId, share.id);

  const validate = ({ confirm }: ShareEditFormValues) => {
    const errors: FormErrors = {};

    if (confirm !== share.name)
      errors.confirm = intl.formatMessage({
        id: "form.confirm.not.same",
        defaultMessage: "Value is not the same as share name",
      });

    return errors;
  };

  // Submit can assume validate returned no errors.
  const submit: SubmitFunction<ShareEditFormValues> = async (_, onError) => {
    mutate(
      { revoked: true },
      {
        onSuccess: () => {
          toList();
        },
        onError,
      }
    );
  };

  return (
    <RanaForm initialValues={INITIAL_VALUES} validate={validate} doSubmit={submit}>
      <RanaModal
        isOpen
        onClose={onClose}
        topBar={
          <FormattedMessage
            id="share.title.revoke"
            defaultMessage="Do you wish to revoke the share {name}?"
            values={{ name: <Text as="strong">{share.name}</Text> }}
          />
        }
        maxW="xl"
        buttons={[
          {
            children: cancelButtonText,
            variant: "tertiary",
            isDisabled: isMutating,
            onClick: back,
          },
          <RanaForm.SubmitButton key="submit" variant="danger" isDisabled={isMutating}>
            {intl.formatMessage({
              id: "share.revoke_share",
              defaultMessage: "Revoke share",
            })}
          </RanaForm.SubmitButton>,
        ]}
      >
        <Flex flexDir="column" gap={2}>
          <Text>
            <FormattedMessage
              id="message.do_you_want_to_revoke_share"
              defaultMessage="Do you really want to revoke this share? This action {cannot} be undone. This will permanently delete the access of the receiver to the publication."
              values={{
                cannot: (
                  <Text as="strong">
                    <FormattedMessage id="cannot" defaultMessage="CANNOT" />
                  </Text>
                ),
              }}
            />
          </Text>
          <RanaForm.Input
            isRequired
            field="confirm"
            label={intl.formatMessage({
              id: "share.revoke_share_label",
              defaultMessage: "Please type in the name of the share to confirm",
            })}
          />
          <RanaForm.Errors />

          {isMutating && <RanaLoading size="md" isCentered />}
        </Flex>
      </RanaModal>
    </RanaForm>
  );
}
